import React from 'react';
import { RadarFusedTargetInfo } from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { isNonEmpty } from '@ais-ui/utils';
import { Separator, TruncatedText } from '@ais-ui/ui-core';
import { LastOriginDisplay } from './LastOriginDisplay';

interface DetailItemProps {
  label: string;
  value: string;
}

const DetailItem = ({ label, value }: DetailItemProps) => {
  return (
    <div className={'flex flex-col mt-1 min-w-0'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <TruncatedText
        text={isNonEmpty(value) ? value : 'Unknown'}
        className="text-xs select-text"
        maxWidth="100%"
      />
    </div>
  );
};

interface IdentificationProps {
  rfInfo: RadarFusedTargetInfo;
}

const Identification = ({ rfInfo }: IdentificationProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-1">
      {rfInfo.isFused && (
        <>
          <DetailItem
            label={messages.shipDetailsBaseNameLabel}
            value={rfInfo.name}
          />
          <DetailItem label="AIS Class" value={rfInfo.aisClass} />
          <DetailItem
            label={messages.shipDetailsBaseCountryLabel}
            value={rfInfo.country}
          />
          <DetailItem
            label={messages.shipDetailsBaseLengthLabel}
            value={rfInfo.length}
          />
          <DetailItem
            label={messages.shipDetailsBaseWidthLabel}
            value={rfInfo.width}
          />
        </>
      )}
      <DetailItem
        label={messages.shipDetailsBaseMMSILabel}
        value={rfInfo.mmsi}
      />
      <DetailItem
        label={messages.reportsDetailsTypeLabel}
        value={rfInfo.targetType}
      />
    </div>
  );
};

interface NavigationDataProps {
  rfInfo: RadarFusedTargetInfo;
}

const NavigationData = ({ rfInfo }: NavigationDataProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-1">
      <DetailItem
        label={messages.shipDetailsBaseSOGLabel}
        value={rfInfo.speed}
      />
      <DetailItem label="Heading" value={rfInfo.heading} />
      <DetailItem
        label={messages.shipDetailsBaseCOGLabel}
        value={rfInfo.course}
      />
    </div>
  );
};

interface ReportingStatusProps {
  rfInfo: RadarFusedTargetInfo;
}

const ReportingStatus = ({ rfInfo }: ReportingStatusProps) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  return (
    <div className="grid grid-cols-2 gap-1">
      <DetailItem
        label="Last Report"
        value={formatDateTimeWithTimeZone(rfInfo.lastReportTimestamp)}
      />
      <DetailItem
        label={messages.shipDetailsBaseLatLabel}
        value={rfInfo.latitudeDegrees}
      />
      <DetailItem
        label={messages.shipDetailsBaseLonLabel}
        value={rfInfo.longitudeDegrees}
      />
    </div>
  );
};

interface SensorInfoProps {
  rfInfo: RadarFusedTargetInfo;
}

const SensorInfo = ({ rfInfo }: SensorInfoProps) => {
  return (
    <div className="grid grid-cols-2 gap-1">
      <DetailItem label="Sensor Track ID" value={rfInfo.sensorTrackId} />
      <DetailItem label="Sensor Site" value={rfInfo.sensorSite} />
      <DetailItem label="Sensor ID" value={rfInfo.sensorId} />
      <DetailItem label="Sensor Type" value={rfInfo.sensorType} />
      <DetailItem label="Main Source" value={rfInfo.sensorMainSource} />
    </div>
  );
};

export const RadarFusedDetailsPanel = ({
  rfInfo,
}: {
  rfInfo: RadarFusedTargetInfo;
}) => {
  return (
    <div className="grid gap-6">
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">
            {rfInfo.isFused
              ? 'AIS & Radar Identification'
              : 'Target Identification'}
          </h3>
          <Separator className="mt-2"></Separator>
        </div>
        <Identification rfInfo={rfInfo} />
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Navigation Data</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <NavigationData rfInfo={rfInfo} />
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Reporting Status</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <ReportingStatus rfInfo={rfInfo} />
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Sensor Information</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <SensorInfo rfInfo={rfInfo} />
      </div>

      {rfInfo.lastOrigin && (
        <LastOriginDisplay lastOrigin={rfInfo.lastOrigin} />
      )}
    </div>
  );
};
